import CloseOutlined from '@mui/icons-material/CloseOutlined';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useRef, useState } from 'react';
import nextUrl from '../../assets/dashboard/page-next.svg';
import prevUrl from '../../assets/dashboard/page-prev.svg';
import type { CreativeDetailTab } from '../../lib/creative-detail-search.js';
import {
  useCreativesToWatch,
  type WatchData,
  type WatchedCreative,
  type WatchScope,
  type WatchStatus,
} from '../../lib/dashboard.hooks.js';
import { formatCompactCurrency, formatCount, formatPercent } from '../../lib/format.js';
import type { CreativeRow } from '../creatives/data/api.js';
import { CreativeDetailDrawer } from '../creatives/detail/CreativeDetailDrawer.js';
import { useDetailKpis } from '../creatives/detail/data/api.js';
import { INFO_TIP_SLOT_PROPS, InfoTip } from '../media/shared/InfoTip.js';
import { MediaCard } from '../media/shared/MediaCard.js';
import { useTrackTooltip } from './TooltipTracking.js';
import { TOOLTIPS } from './tooltips.js';

const PAGE_SIZE = 3;
const SKELETON_KEYS = ['first', 'second', 'third'];
// ponytail: per-tab session storage, as the spec asks; a server-side list if dismissals must outlive it.
const DISMISSED_KEY = 'dashboard.creativesToWatch.dismissed';
const UNDO_MS = 6000;
const MEDIA_HEIGHT = 140;
const MEDIA_FADE = 'linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 60%)';
const STAT_LABEL_SX = {
  fontSize: 10,
  fontWeight: 500,
  color: 'text.secondary',
  textTransform: 'uppercase',
} as const;
const PLATFORM_LABEL: Record<string, string> = { meta: 'Meta', google: 'Google', tiktok: 'TikTok' };
const STATUS: Record<
  WatchStatus,
  { label: string; reason: string; tooltip: string; color: string; bg: string }
> = {
  top_performer: {
    label: 'Top performer',
    reason: 'Beating benchmark',
    tooltip: TOOLTIPS.watchTopPerformer,
    color: 'success.main',
    bg: 'success.bg',
  },
  fatigue: {
    label: 'Fatigue',
    reason: 'Performance fading',
    tooltip: TOOLTIPS.watchFatigue,
    color: 'warning.dark',
    bg: 'warning.bg',
  },
  underperformer: {
    label: 'Underperformer',
    reason: 'Below benchmark',
    tooltip: TOOLTIPS.watchUnderperformer,
    color: 'error.main',
    bg: 'error.bg',
  },
  scaling_candidate: {
    label: 'Scaling candidate',
    reason: 'Under-funded winner',
    tooltip: TOOLTIPS.watchScalingCandidate,
    color: 'info.main',
    bg: 'info.bg',
  },
};

type KpiFormat = WatchData['kpi']['format'];

interface CreativesToWatchProps {
  scope: WatchScope;
  enabled: boolean;
}

interface Stat {
  label: string;
  value: string;
}

interface OpenedCreative {
  creativeId: string;
  tab: CreativeDetailTab;
}

function readDismissed(): string[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: string[]): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable: the dismissal lasts only until the page reloads.
  }
}

function formatKpi(value: number | null, format: KpiFormat, symbol: string): string {
  if (format === 'percentage') return formatPercent(value, 1);
  if (format === 'compact_currency') return formatCompactCurrency(value, symbol);
  if (format === 'ratio') return value === null ? '-' : `${value.toFixed(1)}×`;
  return formatCount(value);
}

function platformLabel(network: string | null): string {
  if (network === null) return 'Unknown platform';
  return PLATFORM_LABEL[network.toLowerCase()] ?? network;
}

function statsOf(creative: WatchedCreative, kpiName: string, symbol: string): Stat[] {
  return [
    { label: 'Spend', value: formatCompactCurrency(creative.spend, symbol) },
    ...(creative.roas === null || kpiName === 'roas'
      ? []
      : [{ label: 'ROAS', value: `${creative.roas.toFixed(1)}×` }]),
    ...(creative.ctr === null || kpiName === 'ctr'
      ? []
      : [{ label: 'CTR', value: formatPercent(creative.ctr, 1) }]),
    { label: 'Days live', value: String(creative.daysLive) },
  ];
}

function detailRow(creative: WatchedCreative): CreativeRow {
  return {
    creativeId: creative.creativeId,
    thumbnailUrl: creative.thumbnailUrl ?? '',
    previewUrl: creative.previewUrl ?? '',
    duration: creative.duration,
    format: creative.format,
    network: creative.network,
    creativeType: creative.creativeType,
    spendRatio: null,
    activeDays: creative.daysLive,
    firstSeen: null,
    lastSeen: null,
    metrics: {},
    uplift: {},
  };
}

function PageButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <ButtonBase
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 32,
        height: 32,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&.Mui-disabled': { opacity: 0.45 },
      }}
    >
      <Box component="img" src={icon} alt="" width={16} height={16} />
    </ButtonBase>
  );
}

function CreativeCard({
  creative,
  data,
  onDismiss,
  onOpen,
}: {
  creative: WatchedCreative;
  data: WatchData;
  onDismiss: () => void;
  onOpen: () => void;
}): ReactNode {
  const status = STATUS[creative.status];
  const trackTooltip = useTrackTooltip();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const creativeType = creative.creativeType?.toLowerCase();
  const previewUrl = creativeType === 'image' ? null : creative.previewUrl || null;
  const isVideo = creativeType === 'video' || previewUrl !== null;

  function handleMouseEnter(): void {
    if (previewUrl === null) return;
    setIsPlaying(true);
    const video = videoRef.current;
    // A superseded play() rejects after a newer one started; only a still-paused video failed.
    video?.play().catch(() => {
      if (video.paused) setIsPlaying(false);
    });
  }

  function handleMouseLeave(): void {
    setIsPlaying(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  return (
    <MediaCard sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box
        data-testid="watch-card"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ position: 'relative', height: MEDIA_HEIGHT, bgcolor: 'background.platform' }}
      >
        {/* Mouse shortcut for "View creative", which stays the keyboard path. */}
        <ButtonBase
          tabIndex={-1}
          aria-label={`Open ${creative.creativeId}`}
          onClick={onOpen}
          sx={{ position: 'absolute', inset: 0, display: 'block' }}
        >
          {creative.thumbnailUrl === null ? null : (
            <Box
              component="img"
              src={creative.thumbnailUrl}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          {previewUrl === null ? null : (
            <Box
              component="video"
              ref={videoRef}
              src={previewUrl}
              muted
              loop
              playsInline
              preload="none"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: isPlaying ? 1 : 0,
                transition: 'opacity 150ms ease',
                pointerEvents: 'none',
              }}
            />
          )}
          <Box sx={{ position: 'absolute', inset: 0, background: MEDIA_FADE }} />
          {isVideo && !isPlaying ? (
            <Box
              data-testid="watch-video-badge"
              sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}
            >
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 40,
                  height: 40,
                }}
              >
                <PlayArrowRounded
                  titleAccess="Video"
                  sx={{ fontSize: 22, color: 'text.primary', ml: '2px' }}
                />
              </Box>
            </Box>
          ) : null}
        </ButtonBase>
        <Tooltip
          title={status.tooltip}
          placement="top-start"
          arrow
          slotProps={INFO_TIP_SLOT_PROPS}
          onOpen={() => trackTooltip(`Creatives to watch · ${status.label}`)}
        >
          <Box
            component="span"
            tabIndex={0}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              px: '6px',
              py: '4px',
              borderRadius: '6px',
              bgcolor: status.bg,
              opacity: 0.7,
              color: status.color,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              cursor: 'help',
            }}
          >
            {status.label}
          </Box>
        </Tooltip>
        <IconButton
          size="small"
          aria-label={`Dismiss ${creative.creativeId}`}
          onClick={onDismiss}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            bgcolor: 'rgba(255,255,255,.85)',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Stack sx={{ p: '16px', gap: '12px', flex: 1 }}>
        <Stack sx={{ gap: '2px', minWidth: 0 }}>
          <Typography
            noWrap
            title={creative.creativeId}
            sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}
          >
            {creative.creativeId}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {platformLabel(creative.network)} · {status.reason}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ gap: '8px', alignItems: 'baseline' }}>
          <Typography sx={{ fontSize: 26, fontWeight: 600, color: status.color, lineHeight: 1.1 }}>
            {formatKpi(creative.kpiValue, data.kpi.format, data.currencySymbol)}
          </Typography>
          <Typography sx={STAT_LABEL_SX}>{data.kpi.label} · primary KPI</Typography>
        </Stack>
        <Box sx={{ height: '1px', bgcolor: 'divider' }} />
        <Box sx={{ display: 'grid', gridAutoFlow: 'column', gap: '16px' }}>
          {statsOf(creative, data.kpi.name, data.currencySymbol).map((stat) => (
            <Stack key={stat.label} sx={{ gap: '2px', minWidth: 0 }}>
              <Typography sx={STAT_LABEL_SX}>{stat.label}</Typography>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                {stat.value}
              </Typography>
            </Stack>
          ))}
        </Box>
        <Button variant="outlined" color="inherit" onClick={onOpen} sx={{ mt: 'auto' }}>
          View creative
        </Button>
      </Stack>
    </MediaCard>
  );
}

function EmptyState({
  dismissedCount,
  onRestore,
}: {
  dismissedCount: number;
  onRestore: () => void;
}): ReactNode {
  return (
    <MediaCard sx={{ p: '32px', textAlign: 'center' }}>
      <Box data-testid="watch-empty">
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {dismissedCount > 0
            ? 'You dismissed every flagged creative'
            : 'Nothing needs attention right now'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: '4px' }}>
          {dismissedCount > 0
            ? 'They stay hidden for this session.'
            : 'No creative in this scope is a top performer, fatiguing, underperforming or a scaling candidate.'}
        </Typography>
        <Stack
          direction="row"
          sx={{ justifyContent: 'center', alignItems: 'center', gap: '16px', mt: '16px' }}
        >
          {dismissedCount > 0 ? (
            <Button variant="text" onClick={onRestore}>
              Restore dismissed
            </Button>
          ) : null}
          <Link
            to="/workflows"
            search={(previous) => previous}
            style={{ color: 'inherit', fontWeight: 600 }}
          >
            Run a workflow →
          </Link>
        </Stack>
      </Box>
    </MediaCard>
  );
}

export function CreativesToWatch({ scope, enabled }: CreativesToWatchProps): ReactNode {
  const query = useCreativesToWatch(scope, enabled);
  const detailKpis = useDetailKpis(scope.accountIds, scope.kpi);
  const [page, setPage] = useState(0);
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const [undo, setUndo] = useState<string | null>(null);
  const [opened, setOpened] = useState<OpenedCreative | null>(null);

  const creatives = query.data?.creatives ?? [];
  const visible = creatives.filter((creative) => !dismissed.includes(creative.creativeId));
  // Dismissals of creatives no longer in this scope say nothing about it.
  const dismissedHere = dismissed.filter((id) => creatives.some((c) => c.creativeId === id));
  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const at = Math.min(page, pages - 1);
  const shown = visible.slice(at * PAGE_SIZE, at * PAGE_SIZE + PAGE_SIZE);

  const setDismissedIds = (ids: string[]): void => {
    setDismissed(ids);
    writeDismissed(ids);
  };
  const handleDismiss = (creativeId: string): void => {
    setDismissedIds([...dismissed, creativeId]);
    setUndo(creativeId);
  };
  const handleUndo = (): void => {
    if (undo !== null) setDismissedIds(dismissed.filter((id) => id !== undo));
    setUndo(null);
  };
  const trackTooltip = useTrackTooltip();

  return (
    <Stack
      component="section"
      aria-labelledby="watch-title"
      data-testid="creatives-to-watch"
      sx={{ gap: '16px' }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-end', gap: '16px' }}>
        <Stack sx={{ flex: 1, gap: '2px' }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Typography id="watch-title" component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
              Creatives to watch
            </Typography>
            <InfoTip
              text={TOOLTIPS.creativesToWatch}
              onOpen={() => trackTooltip('Creatives to watch')}
            />
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Live creatives flagged on the primary KPI: winners to scale, fatigue and waste to fix
          </Typography>
        </Stack>
        {visible.length > PAGE_SIZE ? (
          <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
            <Typography
              data-testid="watch-counter"
              sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}
            >
              {at + 1} / {pages}
            </Typography>
            <PageButton
              icon={prevUrl}
              label="Previous creatives"
              disabled={at === 0}
              onClick={() => setPage(at - 1)}
            />
            <PageButton
              icon={nextUrl}
              label="Next creatives"
              disabled={at >= pages - 1}
              onClick={() => setPage(at + 1)}
            />
          </Stack>
        ) : null}
      </Stack>
      {query.isError ? (
        <MediaCard sx={{ p: '24px' }}>
          <Stack
            direction="row"
            data-testid="watch-error"
            sx={{ alignItems: 'center', gap: '12px' }}
          >
            <Typography sx={{ fontSize: 13, color: 'error.main', flex: 1 }}>
              Creatives to watch could not be loaded.
            </Typography>
            <Button variant="text" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </Stack>
        </MediaCard>
      ) : query.isPending ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${PAGE_SIZE}, minmax(0, 1fr))`,
            gap: '16px',
          }}
        >
          {SKELETON_KEYS.map((key) => (
            <Skeleton key={key} variant="rounded" height={340} sx={{ borderRadius: '14px' }} />
          ))}
        </Box>
      ) : shown.length === 0 ? (
        <EmptyState
          dismissedCount={dismissedHere.length}
          onRestore={() => setDismissedIds(dismissed.filter((id) => !dismissedHere.includes(id)))}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `repeat(${PAGE_SIZE}, minmax(0, 1fr))` },
            gap: '16px',
          }}
        >
          {shown.map((creative) => (
            <CreativeCard
              key={creative.creativeId}
              creative={creative}
              data={query.data}
              onDismiss={() => handleDismiss(creative.creativeId)}
              onOpen={() => setOpened({ creativeId: creative.creativeId, tab: 'overview' })}
            />
          ))}
        </Box>
      )}
      {pages > 1 ? (
        <Stack direction="row" sx={{ justifyContent: 'center', gap: '6px' }} aria-hidden="true">
          {Array.from({ length: pages }, (_, index) => index).map((pageIndex) => (
            <Box
              key={`page-${pageIndex}`}
              sx={{
                width: pageIndex === at ? 18 : 6,
                height: 6,
                borderRadius: '3px',
                bgcolor: pageIndex === at ? 'primary.main' : 'divider',
              }}
            />
          ))}
        </Stack>
      ) : null}
      {/* Keyed per dismissal so the auto-hide timer restarts instead of inheriting the last one. */}
      <Snackbar
        key={undo ?? 'none'}
        open={undo !== null}
        autoHideDuration={UNDO_MS}
        onClose={(_event, reason) => {
          if (reason !== 'clickaway') setUndo(null);
        }}
        message={undo === null ? '' : `Dismissed ${undo}`}
        action={
          <Button size="small" onClick={handleUndo}>
            Undo
          </Button>
        }
      />
      {opened === null || query.data === undefined ? null : (
        <CreativeDetailDrawer
          creatives={visible.map(detailRow)}
          creativeId={opened.creativeId}
          tab={opened.tab}
          scope={{
            dateRange: scope.dateRange,
            ...(scope.accountIds ? { accountIds: scope.accountIds } : {}),
            ...(scope.filters ? { filters: scope.filters } : {}),
          }}
          kpis={detailKpis}
          leadingKpiLabel={query.data.kpi.label}
          onOpen={(creativeId) => setOpened({ ...opened, creativeId })}
          onTabChange={(tab) => setOpened({ ...opened, tab })}
          onClose={() => setOpened(null)}
        />
      )}
    </Stack>
  );
}
